import React from 'react';
import { Card, Input, Tag, Button, Row, Col, message, Switch } from 'antd';
import { _postUpdateEnterpriseCoretaxCustomFields, _postUpdateEnterpriseLhdnCustomFields } from '../../services';

class EnterpriseCustomFields extends React.Component {
    constructor(props) {
        super(props);

        const coretaxCompanyMappings = props.enterprise.customFields.coretax;
        const lhdnCustomFields = props.enterprise.customFields.lhdn || { lhdnTin: '', lhdnBrn: '' };

        this.state = {
            // CORETAX
            coretaxIsEditMode: false,
            coretaxLoading: false,
            coretaxCompanyMappings,
            newNpwpId: '',
            newBillToCompanyName: '',
            editingBillToCompanyNameIndex: null,

            // LHDN
            lhdnIsEditMode: false,
            lhdnLoading: false,
            lhdnRequireEInvoice: lhdnCustomFields.lhdnRequireEInvoice || false,
            lhdnTin: lhdnCustomFields.lhdnTin || '',
            lhdnBrn: lhdnCustomFields.lhdnBrn || ''
        };
    }


    // Add a new NPWP ID entry
    handleAddNpwpIdEntry = () => {
        const { newNpwpId, coretaxCompanyMappings } = this.state;

        if (!newNpwpId.trim()) {
            message.warning('Please enter an NPWP ID');
            return;
        }

        // Check if NPWP ID already exists
        if (coretaxCompanyMappings.some(entry => entry.coretaxNpwpId === newNpwpId.trim())) {
            message.warning('This NPWP ID already exists');
            return;
        }

        // Add new entry with empty company names array
        const _coretaxCompanyMappings = [
            ...coretaxCompanyMappings,
            {
                coretaxNpwpId: newNpwpId.trim(),
                coretaxBillToCompanyNames: []
            }
        ];

        this.setState({
            coretaxCompanyMappings: _coretaxCompanyMappings,
            newNpwpId: ''
        });

    };

    // Remove an NPWP ID entry and its associated company names
    handleRemoveNpwpEntry = (index) => {
        const { coretaxCompanyMappings } = this.state;

        const updatedEntries = [
            ...coretaxCompanyMappings.slice(0, index),
            ...coretaxCompanyMappings.slice(index + 1)
        ];

        this.setState({
            coretaxCompanyMappings: updatedEntries
        });

    };

    // Add a company name to a specific NPWP ID entry
    handleAddCompanyName = (index) => {
        const { newBillToCompanyName, coretaxCompanyMappings } = this.state;

        // Check that input isn't just whitespace
        if (!newBillToCompanyName.trim()) {
            message.warning('Please enter a company name');
            return;
        }

        // Use the original input with preserved whitespace and capitalization
        // Only trim leading/trailing spaces but preserve internal spacing and case
        const companyName = newBillToCompanyName.trim();

        // For comparison to check duplicates, normalize the strings
        const normalizedInput = companyName.toLowerCase();
        const existingNames = coretaxCompanyMappings[index].coretaxBillToCompanyNames.map(name =>
            name.toLowerCase()
        );

        // Check if company name already exists in this NPWP ID entry (case-insensitive comparison)
        if (existingNames.includes(normalizedInput)) {
            message.warning('This company name already exists for this NPWP ID');
            return;
        }

        // Add company name to the specific NPWP ID entry with preserved formatting
        const _coretaxCompanyMappings = [...coretaxCompanyMappings];
        _coretaxCompanyMappings[index] = {
            ..._coretaxCompanyMappings[index],
            coretaxBillToCompanyNames: [..._coretaxCompanyMappings[index].coretaxBillToCompanyNames, companyName]
        };

        this.setState({
            coretaxCompanyMappings: _coretaxCompanyMappings,
            newBillToCompanyName: '',
            editingBillToCompanyNameIndex: null,
            hasUnsavedChanges: true
        });
    };

    // Remove a company name from a specific NPWP ID entry
    handleRemoveCompanyName = (entryIndex, nameIndex) => {
        const { coretaxCompanyMappings } = this.state;

        const _coretaxCompanyMappings = [...coretaxCompanyMappings];
        const _coretaxBillToCompanyNames = [
            ..._coretaxCompanyMappings[entryIndex].coretaxBillToCompanyNames.slice(0, nameIndex),
            ..._coretaxCompanyMappings[entryIndex].coretaxBillToCompanyNames.slice(nameIndex + 1)
        ];

        _coretaxCompanyMappings[entryIndex] = {
            ..._coretaxCompanyMappings[entryIndex],
            coretaxBillToCompanyNames: _coretaxBillToCompanyNames
        };

        this.setState({
            coretaxCompanyMappings: _coretaxCompanyMappings,
            hasUnsavedChanges: true
        });
    };

    // Set which NPWP ID entry is currently having company names edited
    handleSetEditingBillToCompanyNameIndex = (index) => {
        this.setState({
            editingBillToCompanyNameIndex: index,
            newBillToCompanyName: ''
        });
    };

    // CORETAX: Start editing
    handleCoretaxStartEditing = () => {
        this.setState({
            coretaxIsEditMode: true,
        });
    };

    // CORETAX: Cancel editing
    handleCoretaxCancelEditing = () => {
        const coretaxCompanyMappings = this.props.enterprise.customFields.coretax;
        this.setState({
            coretaxIsEditMode: false,
            coretaxCompanyMappings,
            newNpwpId: '',
            newBillToCompanyName: '',
            editingBillToCompanyNameIndex: null
        });
    };


    // Update an NPWP ID for a specific entry
    handleUpdateNpwpId = (index, value) => {
        const { coretaxCompanyMappings } = this.state;

        const updatedEntries = [...coretaxCompanyMappings];
        updatedEntries[index] = {
            ...updatedEntries[index],
            coretaxNpwpId: value
        };

        this.setState({
            coretaxCompanyMappings: updatedEntries,
            hasUnsavedChanges: true
        });
    };

    // CORETAX: Save changes
    handleCoretaxSaveChanges = () => {
        const { coretaxCompanyMappings } = this.state;

        // Validate all Coretax entries
        for (let i = 0; i < coretaxCompanyMappings.length; i++) {
            // Check if any NPWP ID is empty
            if (!coretaxCompanyMappings[i].coretaxNpwpId.trim()) {
                message.warning(`NPWP ID at position ${i + 1} cannot be empty`);
                return;
            }

            // Check for duplicate NPWP IDs
            for (let j = i + 1; j < coretaxCompanyMappings.length; j++) {
                if (coretaxCompanyMappings[i].coretaxNpwpId === coretaxCompanyMappings[j].coretaxNpwpId) {
                    message.warning(`Duplicate NPWP ID found: ${coretaxCompanyMappings[i].coretaxNpwpId}`);
                    return;
                }
            }
        }

        this.postCoretaxCustomData(coretaxCompanyMappings);
    };

    // This method has been replaced by handleSaveChanges

    // Save the coretax entries to the backend
    postCoretaxCustomData = async (entries) => {
        this.setState({ coretaxLoading: true });

        try {
            // Convert entries format to backend format
            const coretaxArray = entries.map(entry => {
                // For each entry, create a single object with both the NPWP ID and company names
                return {
                    coretaxNpwpId: entry.coretaxNpwpId,
                    coretaxBillToCompanyNames: entry.coretaxBillToCompanyNames || []
                };
            });

            await _postUpdateEnterpriseCoretaxCustomFields({
                enterpriseId: this.props.enterprise._id,
                coretaxCustomFields: coretaxArray
            });

            message.success('Coretax information updated successfully');

            this.setState({ coretaxIsEditMode: false });
            this.props.onUpdateSuccess();

        } catch (err) {
            message.error('Failed to update coretax information');
            console.error(err);
        } finally {
            this.setState({ coretaxLoading: false });
        }
    };

    // LHDN: Start editing
    handleLhdnStartEditing = () => {
        this.setState({
            lhdnIsEditMode: true,
        });
    };

    // LHDN: Cancel editing
    handleLhdnCancelEditing = () => {
        const lhdnCustomFields = this.props.enterprise.customFields.lhdn || { lhdnTin: '', lhdnBrn: '' };
        this.setState({
            lhdnIsEditMode: false,
            lhdnRequireEInvoice: lhdnCustomFields.lhdnRequireEInvoice || false,
            lhdnTin: lhdnCustomFields.lhdnTin || '',
            lhdnBrn: lhdnCustomFields.lhdnBrn || ''
        });
    };

    // LHDN: Save changes
    handleLhdnSaveChanges = () => {
        const { lhdnRequireEInvoice, lhdnTin, lhdnBrn } = this.state;

        // Validate LHDN fields
        if (!lhdnTin.trim()) {
            message.warning('LHDN TIN cannot be empty');
            return;
        }

        if (!lhdnBrn.trim()) {
            message.warning('LHDN BRN cannot be empty');
            return;
        }

        this.postLhdnCustomData({
            lhdnRequireEInvoice: Boolean(lhdnRequireEInvoice),
            lhdnTin: lhdnTin.trim(),
            lhdnBrn: lhdnBrn.trim()
        });
    };

    // Save the LHDN data to the backend
    postLhdnCustomData = async (lhdnData) => {
        this.setState({ lhdnLoading: true });

        try {
            await _postUpdateEnterpriseLhdnCustomFields({
                enterpriseId: this.props.enterprise._id,
                lhdnCustomFields: lhdnData
            });

            message.success('LHDN information updated successfully');

            this.setState({ lhdnIsEditMode: false });
            this.props.onUpdateSuccess();

        } catch (err) {
            message.error('Failed to update LHDN information');
            console.error(err);
        } finally {
            this.setState({ lhdnLoading: false });
        }
    };

    render() {
        const {
            coretaxCompanyMappings,
            newNpwpId,
            newBillToCompanyName,
            editingBillToCompanyNameIndex,
            coretaxLoading,
            coretaxIsEditMode,
            lhdnTin,
            lhdnBrn,
            lhdnLoading,
            lhdnIsEditMode
        } = this.state;

        return (
            <Row gutter={[24, 24]}>
                {/* Coretax NPWP ID Section */}
                <Col span={24}>
                    <Card
                        type="inner"
                        title="Coretax"
                        extra={
                            coretaxIsEditMode ? (
                                <span>
                                    <Button
                                        type="primary"
                                        onClick={this.handleCoretaxSaveChanges}
                                        style={{ marginRight: 8 }}
                                        disabled={coretaxLoading}
                                        icon="save"
                                    >
                                        Save Changes
                                    </Button>
                                    <Button
                                        onClick={this.handleCoretaxCancelEditing}
                                        disabled={coretaxLoading}
                                        icon="close"
                                    >
                                        Cancel
                                    </Button>
                                </span>
                            ) : (
                                <Button
                                    type="primary"
                                    onClick={this.handleCoretaxStartEditing}
                                    disabled={coretaxLoading}
                                    icon="edit"
                                >
                                    Edit
                                </Button>
                            )
                        }
                    >
                        {/* Add new NPWP ID section - only shown in edit mode */}
                        {coretaxIsEditMode && (
                            <Row gutter={8} style={{ marginBottom: 24 }}>
                                <Col flex="auto">
                                    <Input
                                        value={newNpwpId}
                                        onChange={(e) => this.setState({ newNpwpId: e.target.value })}
                                        placeholder="Enter a new NPWP ID"
                                        onPressEnter={this.handleAddNpwpIdEntry}
                                    />
                                </Col>
                                <Col>
                                    <Button
                                        type="primary"
                                        onClick={this.handleAddNpwpIdEntry}
                                        disabled={coretaxLoading}
                                        icon="plus"
                                    >
                                        Add NPWP ID
                                    </Button>
                                </Col>
                            </Row>
                        )}

                        {/* NPWP ID entries */}
                        {coretaxCompanyMappings.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#999', fontStyle: 'italic', padding: '20px 0' }}>
                                No NPWP IDs added yet
                            </div>
                        ) : (
                            coretaxCompanyMappings.map((entry, entryIndex) => (
                                <Card
                                    key={entryIndex}
                                    type="inner"
                                    style={{ marginBottom: 16 }}
                                    title={
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <strong>NPWP ID:</strong>
                                                <Input
                                                    value={entry.coretaxNpwpId}
                                                    onChange={(e) => this.handleUpdateNpwpId(entryIndex, e.target.value)}
                                                    disabled={!coretaxIsEditMode}
                                                    style={{ width: 300, marginLeft: 8 }}
                                                />
                                            </div>
                                            <div>
                                                {coretaxIsEditMode && (
                                                    <Button
                                                        danger
                                                        onClick={() => this.handleRemoveNpwpEntry(entryIndex)}
                                                        icon="delete"
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    }
                                >
                                    <div>
                                        <h4>ERP Sales Order Bill To Company Names</h4>
                                        {coretaxIsEditMode && (
                                            editingBillToCompanyNameIndex === entryIndex ? (
                                                <Row gutter={8} style={{ marginBottom: 12 }}>
                                                    <Col flex="auto">
                                                        <Input
                                                            value={newBillToCompanyName}
                                                            onChange={(e) => this.setState({ newBillToCompanyName: e.target.value })}
                                                            placeholder="Enter company name"
                                                            onPressEnter={() => this.handleAddCompanyName(entryIndex)}
                                                        />
                                                    </Col>
                                                    <Col>
                                                        <Button
                                                            type="primary"
                                                            onClick={() => this.handleAddCompanyName(entryIndex)}
                                                            disabled={coretaxLoading}
                                                        >
                                                            Add
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ) : (
                                                <Button
                                                    type="dashed"
                                                    style={{ marginBottom: 12 }}
                                                    onClick={() => this.handleSetEditingBillToCompanyNameIndex(entryIndex)}
                                                >
                                                    Add Company Name
                                                </Button>
                                            )
                                        )}

                                        <div>
                                            {entry.coretaxBillToCompanyNames.length === 0 ? (
                                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                    No company names added yet
                                                </span>
                                            ) : (
                                                entry.coretaxBillToCompanyNames.map((companyName, nameIndex) => (
                                                    <Tag
                                                        key={nameIndex}
                                                        closable={coretaxIsEditMode}
                                                        onClose={coretaxIsEditMode ?
                                                            () => this.handleRemoveCompanyName(entryIndex, nameIndex) :
                                                            undefined}
                                                        style={{
                                                            marginBottom: 8,
                                                            padding: '4px 8px',
                                                            fontSize: '13px',
                                                            whiteSpace: 'pre-wrap', // Preserve whitespace including spaces
                                                            wordBreak: 'break-word' // Allow words to break if needed
                                                        }}
                                                    >
                                                        {companyName}
                                                    </Tag>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            ))
                        )}
                    </Card>
                </Col>

                {/* LHDN Section */}
                <Col span={24}>
                    <Card
                        type="inner"
                        title="LHDN (Lembaga Hasil Dalam Negeri)"
                        extra={
                            lhdnIsEditMode ? (
                                <span>
                                    <Button
                                        type="primary"
                                        onClick={this.handleLhdnSaveChanges}
                                        style={{ marginRight: 8 }}
                                        disabled={lhdnLoading}
                                        icon="save"
                                    >
                                        Save Changes
                                    </Button>
                                    <Button
                                        onClick={this.handleLhdnCancelEditing}
                                        disabled={lhdnLoading}
                                        icon="close"
                                    >
                                        Cancel
                                    </Button>
                                </span>
                            ) : (
                                <Button
                                    type="primary"
                                    onClick={this.handleLhdnStartEditing}
                                    disabled={lhdnLoading}
                                    icon="edit"
                                >
                                    Edit
                                </Button>
                            )
                        }
                    >
                        <Row gutter={[16, 16]}>
                             <Col span={24}>
                                <div style={{ marginTop: 8 }}>
                                    <label style={{ display: 'inline-block', marginRight: 12, fontWeight: 'bold' }}>
                                        Require E-Invoice
                                    </label>
                                    <Switch
                                        checked={this.state.lhdnRequireEInvoice}
                                        onChange={(checked) => this.setState({ lhdnRequireEInvoice: checked })}
                                        disabled={!lhdnIsEditMode}
                                        checkedChildren="Yes"
                                        unCheckedChildren="No"
                                    />
                                </div>
                            </Col>
                            <Col span={12}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                                        Tax Identification Number (TIN)
                                    </label>
                                    <Input
                                        value={lhdnTin}
                                        onChange={(e) => this.setState({ lhdnTin: e.target.value })}
                                        placeholder="Enter TIN"
                                        disabled={!lhdnIsEditMode}
                                        size="large"
                                    />
                                </div>
                            </Col>
                            <Col span={12}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                                        Business Registration Number (BRN)
                                    </label>
                                    <Input
                                        value={lhdnBrn}
                                        onChange={(e) => this.setState({ lhdnBrn: e.target.value })}
                                        placeholder="Enter BRN"
                                        disabled={!lhdnIsEditMode}
                                        size="large"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>

            </Row>
        );
    }
}

export default EnterpriseCustomFields;



// WEBPACK FOOTER //
// ./src/components/enterprise/custom-fields.js